/*
  Alepu navigation.

  Desktop: a mega panel under the bar showing the group for the item being
  hovered, and nothing else — hover on pointer devices, click and keyboard
  everywhere.
  Mobile: a drawer whose second level is a native <details> accordion, so the
  open/close behaviour costs no script and survives without JS.

  Both are re-bound on shopify:section:load, and both are scoped to the header
  section that fired it rather than to the document.
*/
(function () {
  var HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var OPEN_DELAY = 90;
  var CLOSE_DELAY = 220;

  /* ----------------------------------------------------------- mega panel */

  function bindMega(root) {
    var wrapper = root.querySelector('[data-mega-scope]');
    if (!wrapper || wrapper.dataset.megaBound === 'true') return;

    var panel = wrapper.querySelector('[data-mega-panel]');
    var nav = wrapper.querySelector('.alepu-nav');
    var toggles = wrapper.querySelectorAll('[data-mega-toggle]');
    var groups = wrapper.querySelectorAll('[data-mega-group]');
    /* Every top-level item, including any that names no group — hovering one of
       those has to close whatever is open rather than leave it hanging. */
    var items = wrapper.querySelectorAll('.alepu-nav__item');
    if (!panel || !nav || !toggles.length) return;

    wrapper.dataset.megaBound = 'true';

    var openTimer = null;
    var closeTimer = null;
    var current = null;

    function clearTimers() {
      if (openTimer) clearTimeout(openTimer);
      if (closeTimer) clearTimeout(closeTimer);
      openTimer = null;
      closeTimer = null;
    }

    function setActive(item) {
      items.forEach(function (el) {
        el.classList.toggle('is-active', !!item && el.dataset.megaTarget === item.dataset.megaTarget);
      });
    }

    /* The panel is one element carrying one group per top-level item, so
       opening it is really a matter of which group is shown. */
    function open(toggle) {
      clearTimers();
      if (!toggle) return;

      var target = toggle.dataset.megaTarget;
      groups.forEach(function (group) {
        group.hidden = group.dataset.megaGroup !== target;
      });

      setActive(toggle);
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', String(t.dataset.megaTarget === target));
      });
      current = toggle;

      if (panel.hidden) {
        panel.hidden = false;
        /* Reading offsetHeight commits the unhide before the class lands, so
           the transition runs instead of being skipped. */
        void panel.offsetHeight;
      }
      panel.classList.add('is-open');
    }

    /* Moving along an already-open row should track the pointer immediately;
       the delay only exists to stop a pass-through opening the panel at all. */
    function scheduleOpen(toggle) {
      clearTimers();
      if (!panel.hidden && panel.classList.contains('is-open')) {
        open(toggle);
        return;
      }
      openTimer = setTimeout(function () {
        openTimer = null;
        open(toggle);
      }, OPEN_DELAY);
    }

    function close() {
      clearTimers();
      if (panel.hidden) return;

      panel.classList.remove('is-open');
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      setActive(null);
      current = null;

      /* Kept in the tree until the fade finishes, then taken out of it so the
         links below stop being tabbable. */
      closeTimer = setTimeout(function () {
        closeTimer = null;
        if (!panel.classList.contains('is-open')) panel.hidden = true;
      }, 260);
    }

    function scheduleClose() {
      clearTimers();
      closeTimer = setTimeout(close, CLOSE_DELAY);
    }

    nav.addEventListener('mouseleave', function () {
      if (HOVER.matches) scheduleClose();
    });

    items.forEach(function (item) {
      /* Buttons and the statement links alike — anything that names a group. */
      var opens = item.hasAttribute('data-mega-target');

      item.addEventListener('mouseenter', function () {
        if (!HOVER.matches) return;
        if (opens) {
          scheduleOpen(item);
        } else {
          scheduleClose();
        }
      });

      item.addEventListener('focus', function () {
        if (opens) {
          open(item);
        } else {
          close();
        }
      });
    });

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function (event) {
        event.preventDefault();
        var showing = !panel.hidden && panel.classList.contains('is-open');
        if (showing && current && current.dataset.megaTarget === toggle.dataset.megaTarget) {
          close();
        } else {
          open(toggle);
        }
      });
    });

    panel.addEventListener('mouseenter', clearTimers);
    panel.addEventListener('mouseleave', function () {
      if (HOVER.matches) scheduleClose();
    });

    wrapper.addEventListener('focusout', function () {
      /* Runs after focus has landed, so activeElement is the new target. */
      setTimeout(function () {
        if (!wrapper.contains(document.activeElement)) close();
      });
    });

    /* Both listeners outlive a Theme Editor section reload, which replaces the
       header. Bailing on a detached wrapper is cheaper than tracking them. */
    document.addEventListener('keydown', function (event) {
      if (!wrapper.isConnected || event.key !== 'Escape' || panel.hidden) return;
      var toggle = current;
      close();
      if (toggle) toggle.focus();
    });

    document.addEventListener('click', function (event) {
      if (!wrapper.isConnected || panel.hidden) return;
      if (!wrapper.contains(event.target)) close();
    });
  }

  /* --------------------------------------------------------- mobile drawer */

  var lockCount = 0;

  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    document.body.classList.toggle('alepu-drawer-locked', lockCount > 0);
  }

  class AlepuDrawer extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-drawer-toggle]');
      this.panel = this.querySelector('[data-drawer-panel]');
      this.scrim = this.querySelector('[data-drawer-scrim]');
      if (!this.toggle || !this.panel) return;

      this.isOpen = false;
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.toggle.addEventListener('click', this.onToggle);
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
      document.addEventListener('keydown', this.onKeydown);

      /* Any navigation out of the drawer should not leave it open behind the
         new page in a bfcache restore. */
      this.panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) this.close();

        const expand = event.target.closest('[data-drawer-expand]');
        if (!expand) return;
        const list = document.getElementById(expand.getAttribute('aria-controls'));
        const opening = expand.getAttribute('aria-expanded') !== 'true';
        expand.setAttribute('aria-expanded', String(opening));
        list.hidden = !opening;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      if (this.isOpen) lockScroll(false);
    }

    onToggle(event) {
      event.preventDefault();
      this.isOpen ? this.close() : this.open();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) this.close();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.panel.hidden = false;
      void this.panel.offsetHeight;
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      /* Trapped on the element, not the panel — the close control is the
         toggle, and it sits outside the panel. */
      if (typeof trapFocus === 'function') trapFocus(this, this.panel.querySelector('a, button'));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.toggle);

      var panel = this.panel;
      setTimeout(function () {
        if (!panel.closest('.is-open')) panel.hidden = true;
      }, 320);
    }
  }

  if (!customElements.get('alepu-drawer')) customElements.define('alepu-drawer', AlepuDrawer);

  /* ----------------------------------------------------------------- run */

  function init(root) {
    bindMega(root || document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
